import { UsersIcon } from 'lucide-react'
import { memo, type ReactNode, useCallback } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'

import type { RemoveSeatsWarningProps } from './billing-seat.interface'

const BillingRemoveSeatsWarningBase: React.FC<RemoveSeatsWarningProps> = ({
  isOpen,
  onOpenChange,
  currentMemberCount,
  onGoToMembers,
  className,
}): ReactNode => {
  const handleGoToMembers = useCallback((): void => {
    onOpenChange(false)
    onGoToMembers()
  }, [onGoToMembers, onOpenChange])

  const handleCancel = useCallback((): void => {
    onOpenChange(false)
  }, [onOpenChange])

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className={cn('billing-remove-seats-warning', className)}>
        <AlertDialogHeader>
          <AlertDialogTitle className="billing-remove-seats-warning-title flex items-center gap-2">
            <UsersIcon className="size-5" />
            Cannot Remove Seats
          </AlertDialogTitle>
          <AlertDialogDescription className="billing-remove-seats-warning-description">
            You currently have {currentMemberCount}{' '}
            {currentMemberCount === 1 ? 'member' : 'members'} in your organization. You cannot
            reduce seats below your current member count.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="billing-remove-seats-warning-hint text-sm text-muted-foreground">
          Remove members first to reduce your seat count.
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} className="billing-remove-seats-warning-cancel">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleGoToMembers}
            className="billing-remove-seats-warning-confirm"
          >
            Go to Members
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export const BillingRemoveSeatsWarning = memo(BillingRemoveSeatsWarningBase)

BillingRemoveSeatsWarning.displayName = 'BillingRemoveSeatsWarning'
